import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function useFormAI() {
  const [generating, setGenerating] = useState(false);

  const generateForm = async (prompt: string, orgId: string, category?: string) => {
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-form-with-ai", {
        body: { prompt, orgId, category },
      });

      if (error) throw error;

      if (!data.success) {
        throw new Error(data.error || "Failed to generate form");
      }

      toast.success("Form generated successfully!");
      return data.form;
    } catch (error: any) {
      console.error("Error generating form:", error);
      toast.error(error.message || "Failed to generate form");
      return null;
    } finally {
      setGenerating(false);
    }
  };

  const prefillFormFromJob = async (jobId: string, formFields: any[]) => {
    try {
      // Fetch job data
      const { data: job, error } = await supabase
        .from("jobs")
        .select("*")
        .eq("id", jobId)
        .single();

      if (error) throw error;

      // Map job data to form fields
      const prefilledData: Record<string, any> = {};

      formFields.forEach((field) => {
        // Smart mapping based on field labels
        const label = field.label.toLowerCase();

        if (label.includes("property address") || label.includes("address")) {
          // Construct complete address
          const addressParts = [
            job.property_address,
            job.property_city,
            job.property_state,
            job.property_zip
          ].filter(Boolean);
          prefilledData[field.id] = addressParts.join(", ");
        } else if (label.includes("customer name") || label.includes("owner name")) {
          prefilledData[field.id] = job.owner_name;
        } else if (label.includes("phone")) {
          prefilledData[field.id] = job.owner_phone;
        } else if (label.includes("email")) {
          prefilledData[field.id] = job.owner_email;
        } else if (label.includes("insurance company")) {
          prefilledData[field.id] = job.insurance_company;
        } else if (label.includes("claim number")) {
          prefilledData[field.id] = job.claim_number;
        } else if (label.includes("policy number")) {
          prefilledData[field.id] = job.policy_number;
        } else if (label.includes("roof type")) {
          prefilledData[field.id] = job.roof_type;
        } else if (label.includes("roof age")) {
          prefilledData[field.id] = job.roof_age;
        } else if (label.includes("job number")) {
          prefilledData[field.id] = job.job_number;
        } else if (label.includes("inspection date") || label.includes("assessment date")) {
          prefilledData[field.id] = new Date().toISOString().split('T')[0];
        }
      });

      return prefilledData;
    } catch (error: any) {
      console.error("Error prefilling form:", error);
      toast.error("Failed to prefill form data");
      return {};
    }
  };

  return {
    generateForm,
    prefillFormFromJob,
    generating,
  };
}
